/* PNX: product description layout and scoped navigation. No cart/price requests. */
(() => {
  'use strict';
  if (window.__pnxPdpNavigationInstalled) return;
  window.__pnxPdpNavigationInstalled = true;

  const desktop = window.matchMedia('(min-width: 1024px)');
  const wideStates = new WeakMap();
  const pendingSizeRoots = new WeakMap();
  const commerce = 'form[action*="/cart"], product-form, variant-selects, variant-radios, product-recommendations, .product__block--price, .product__block--buy_buttons, .product__block--variant_picker, [samitaWS-product-unit-price]';

  function destination(link, page) {
    const id = link.hash.slice(1);
    const target = id ? document.getElementById(id) : null;
    return target && page.contains(target) ? target : null;
  }

  function initNavigation(main) {
    main.querySelectorAll('[data-pnx-pdp-navigation]').forEach((nav) => {
      const page = nav.closest('.pnx-pdp');
      if (!page) return;
      let visible = 0;
      nav.querySelectorAll('a[data-pnx-pdp-jump]').forEach((link) => {
        link.hidden = !destination(link, page);
        if (!link.hidden) visible += 1;
      });
      nav.hidden = visible === 0;
    });
  }

  function restoreDescription(main) {
    const state = wideStates.get(main);
    if (!state) return;
    state.entries.forEach(({ block, placeholder }) => {
      if (placeholder.isConnected && main.contains(placeholder)) {
        placeholder.parentNode.insertBefore(block, placeholder);
        placeholder.remove();
        delete block.dataset.pnxWideKind;
      }
    });
    state.container.remove();
    main.classList.remove('pnx-pdp--description-wide');
    main.closest('.pnx-pdp')?.classList.remove('pnx-pdp--wide-prototype');
    wideStates.delete(main);
  }

  function syncDescription(main) {
    let state = wideStates.get(main);
    if (state && (!main.contains(state.container) || state.entries.some(({ placeholder }) => !main.contains(placeholder)))) {
      // A native section replacement invalidates old references; never restore stale blocks.
      restoreDescription(main);
      state = null;
    }
    if (!desktop.matches) {
      restoreDescription(main);
      return;
    }
    if (state) return;

    const product = main.querySelector(':scope > .product');
    const source = product?.querySelector('.product__blocks');
    const heading = source?.querySelector('#pnx-pdp-description')?.closest('.product__block');
    const description = source?.querySelector(':scope > .product__block--description');
    const back = source?.querySelector('.pnx-pdp__return')?.closest('.product__block');
    if (!product || !source || !heading || !description || !back) return;
    const blocks = [heading, description, back];
    if (blocks.some((block) => block.parentElement !== source || block.matches(commerce) || block.querySelector(commerce))) return;
    if (heading.nextElementSibling !== description || description.nextElementSibling !== back) return;
    if (!description.textContent.trim() && !description.querySelector('img, picture, video, iframe, svg')) return;

    const container = document.createElement('div');
    container.className = 'pnx-pdp__wide-details pnx-pdp__wide-description';
    container.setAttribute('data-pnx-pdp-wide-prototype', 'description-only');
    product.insertAdjacentElement('afterend', container);
    const kinds = ['description-heading', 'description', 'return'];
    const entries = blocks.map((block, index) => {
      const placeholder = document.createComment('pnx-description-origin');
      source.insertBefore(placeholder, block);
      block.dataset.pnxWideKind = kinds[index];
      container.appendChild(block);
      return { block, placeholder };
    });
    wideStates.set(main, { container, entries });
    main.classList.add('pnx-pdp--description-wide');
    main.closest('.pnx-pdp')?.classList.add('pnx-pdp--wide-prototype');
  }

  function compactSizeTools(root) {
    if (root.dataset.pnxWidthFirst === '1') return true;
    // The existing size-index v3 owns parsing, tolerance, rows and input events.
    // Enhance only after its real width choices are available. Otherwise leave it intact.
    if (root.dataset.siReady !== '1') return false;
    const tools = root.querySelector('.size-index__tools');
    const widths = tools?.querySelector('.size-index__widths');
    const chips = widths?.querySelector('.size-index__widths-chips');
    const searchWrap = tools?.querySelector('.size-index__search-wrap');
    const search = searchWrap?.querySelector('.size-index__search');
    const dims = tools?.querySelector('.size-index__dims');
    const width = dims?.querySelector('[data-dim="w"]');
    const length = dims?.querySelector('[data-dim="l"]');
    if (!tools || !widths || widths.hidden || !chips?.querySelector('button') || !search || !dims || !width || !length) return false;

    const advanced = document.createElement('details');
    advanced.className = 'pnx-pdp__size-advanced';
    const summary = document.createElement('summary');
    summary.textContent = '品番・幅を入力して探す';
    const activeNote = document.createElement('span');
    activeNote.className = 'pnx-pdp__filter-active';
    activeNote.textContent = '品番で絞り込み中';
    activeNote.hidden = true;
    summary.appendChild(activeNote);
    advanced.appendChild(summary);
    const advancedBody = document.createElement('div');
    advancedBody.className = 'pnx-pdp__size-advanced-body';
    advanced.appendChild(advancedBody);
    advancedBody.appendChild(searchWrap);
    const widthLabel = document.createElement('span');
    widthLabel.textContent = '幅を直接入力（mm）';
    dims.prepend(widthLabel);
    advancedBody.appendChild(dims);

    const extra = document.createElement('div');
    extra.className = 'pnx-pdp__size-options';
    const selected = document.createElement('span');
    selected.className = 'pnx-pdp__selected-width';
    selected.setAttribute('aria-live', 'polite');
    const lengthLabel = document.createElement('label');
    lengthLabel.className = 'pnx-pdp__length-field';
    const caption = document.createElement('span');
    caption.textContent = '長さ（任意・mm）';
    lengthLabel.append(caption, length);
    const reset = document.createElement('button');
    reset.type = 'button';
    reset.className = 'pnx-pdp__filter-reset';
    reset.textContent = '絞り込みを解除';
    extra.append(selected, lengthLabel, reset);

    const label = tools.querySelector('.size-index__tools-label');
    if (label) label.textContent = '幅からサイズを選ぶ';
    const widthsLabel = widths.querySelector('.size-index__widths-label');
    if (widthsLabel) widthsLabel.textContent = 'このシリーズにある幅（mm）';
    if (label) label.after(widths);
    else tools.prepend(widths);
    widths.after(extra);
    const result = tools.querySelector('.size-index__result');
    const tolerance = tools.querySelector('.size-index__tol-note');
    if (result) extra.after(result);
    if (tolerance) (result || extra).after(tolerance);
    tools.appendChild(advanced);

    function syncSelection() {
      selected.textContent = width.value ? `選択中の幅：${width.value} mm` : '幅：指定なし';
      activeNote.hidden = !search.value.trim();
      reset.disabled = !width.value && !length.value && !search.value.trim();
    }
    reset.addEventListener('click', () => {
      [search, width, length].forEach((input) => { input.value = ''; });
      [search, width, length].forEach((input) => {
        input.dispatchEvent(new Event('input', { bubbles: true }));
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
      syncSelection();
    });
    root.addEventListener('input', syncSelection);
    root.addEventListener('change', syncSelection);
    root.addEventListener('click', () => window.setTimeout(syncSelection, 0));
    if (search.value.trim()) advanced.open = true;
    root.dataset.pnxWidthFirst = '1';
    syncSelection();
    return true;
  }

  function simplifyVacuum(main) {
    if (!main.querySelector('.pnx-pdp__identity[data-pnx-vacuum="true"]')) return;
    const redundant = main.querySelector('#pnx-sizes.vacuum-bag-size-container')?.closest('.product__block');
    if (redundant && main.querySelector('#pnx-spec')) {
      redundant.dataset.pnxRedundantSize = 'true';
      redundant.hidden = true;
    }
    const root = main.querySelector('#size-index.size-index');
    if (!root || compactSizeTools(root) || pendingSizeRoots.has(root)) return;
    // Bounded readiness observer. No network or polling of products.
    const observer = new MutationObserver(() => {
      if (!root.isConnected || compactSizeTools(root)) finish();
    });
    let timer;
    function finish() {
      observer.disconnect();
      window.clearTimeout(timer);
      pendingSizeRoots.delete(root);
    }
    pendingSizeRoots.set(root, observer);
    observer.observe(root, { childList: true, subtree: true, attributes: true, attributeFilter: ['data-si-ready', 'hidden'] });
    timer = window.setTimeout(finish, 4000);
  }

  function mainsIn(scope) {
    const mains = new Set();
    if (scope instanceof Element && scope.matches('[id^="MainProduct-"]') && scope.closest('.pnx-pdp')) mains.add(scope);
    scope.querySelectorAll?.('.pnx-pdp [id^="MainProduct-"]').forEach((main) => mains.add(main));
    if (scope instanceof Element) {
      const enclosing = scope.closest('[id^="MainProduct-"]');
      if (enclosing?.closest('.pnx-pdp')) mains.add(enclosing);
    }
    return mains;
  }

  function boot(scope) {
    mainsIn(scope).forEach((main) => {
      simplifyVacuum(main);
      syncDescription(main);
      initNavigation(main);
    });
  }

  document.addEventListener('click', (event) => {
    if (event.defaultPrevented || event.button !== 0 || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
    if (!(event.target instanceof Element)) return;
    const link = event.target.closest('a[data-pnx-pdp-jump]');
    const page = link && link.closest('.pnx-pdp');
    if (!link || !page || link.hidden) return;
    const target = destination(link, page);
    if (!target) return;
    event.preventDefault();
    if (!target.hasAttribute('tabindex')) target.setAttribute('tabindex', '-1');
    target.focus({ preventScroll: true });
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    target.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
    try { window.history.replaceState(window.history.state, '', link.hash); } catch (_) { /* Native links remain usable. */ }
  });

  const onWidthChange = () => boot(document);
  if (desktop.addEventListener) desktop.addEventListener('change', onWidthChange);
  else desktop.addListener(onWidthChange);
  const start = () => { boot(document); window.setTimeout(() => boot(document), 0); };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
  document.addEventListener('shopify:section:load', (event) => {
    if (event.target instanceof Element) {
      boot(event.target);
      window.setTimeout(() => boot(event.target), 0);
    }
  });
  document.addEventListener('shopify:section:unload', (event) => {
    if (event.target instanceof Element) mainsIn(event.target).forEach(restoreDescription);
  });
})();
